import { collection, addDoc, serverTimestamp, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { auth } from '../firebase';

export type AnalyticsEvent =
  | 'listing_view'
  | 'contact_whatsapp'
  | 'contact_call'
  | 'contact_chat'
  | 'search_performed'
  | 'category_view'
  | 'page_view'
  | 'listing_created'
  | 'payment_initiated'
  | 'payment_completed'
  | 'payment_failed'
  | 'user_signup'
  | 'user_login'
  | 'profile_updated'
  | 'listing_edited'
  | 'listing_deleted'
  | 'review_submitted'
  | 'report_submitted'
  | 'hub_filter_applied'
  | 'price_filter_applied'
  | 'location_filter_applied'
  | 'sort_applied'
  | 'favorites_added'
  | 'favorites_removed'
  | 'share_listing'
  | 'ai_description_generated'
  | 'verification_requested'
  | 'plan_upgraded'
  | 'plan_expired'
  | 'error_occurred'
  | 'performance_metric';

export const analyticsService = {
  async logEvent(event: AnalyticsEvent, data: Record<string, any> = {}) {
    try {
      const user = auth.currentUser;
      const eventData = {
        event,
        ...data,
        userId: user?.uid || 'anonymous',
        userRole: (user as any)?.role || 'guest',
        timestamp: serverTimestamp(),
        userAgent: navigator.userAgent,
        platform: navigator.platform,
      };

      await addDoc(collection(db, 'system_analytics'), eventData);
    } catch (error) {
      // Fail silently for analytics to not break UX
      console.warn('Analytics logging failed:', error);
    }
  },

  async logListingContact(
    listingId: string,
    type: 'whatsapp' | 'call' | 'chat',
    sellerId: string,
    category?: string
  ) {
    return this.logEvent(`contact_${type}` as AnalyticsEvent, {
      listingId,
      sellerId,
      category,
      interactionType: type,
    });
  },

  async logPageView(path: string) {
    return this.logEvent('page_view', { path });
  },

  async logSearch(query: string, filters: Record<string, any>, resultsCount: number) {
    return this.logEvent('search_performed', {
      query,
      filters,
      resultsCount,
      timestamp: new Date().toISOString(),
    });
  },

  async logListingAction(
    action: 'created' | 'edited' | 'deleted' | 'viewed',
    listingId: string,
    data?: Record<string, any>
  ) {
    return this.logEvent(`listing_${action}` as AnalyticsEvent, {
      listingId,
      ...data,
    });
  },

  async logPaymentEvent(
    event: 'initiated' | 'completed' | 'failed',
    paymentData: Record<string, any>
  ) {
    return this.logEvent(`payment_${event}` as AnalyticsEvent, {
      ...paymentData,
      timestamp: new Date().toISOString(),
    });
  },

  async logUserAction(
    action: 'signup' | 'login' | 'profile_updated' | 'verification_requested',
    userData?: Record<string, any>
  ) {
    return this.logEvent(`user_${action}` as AnalyticsEvent, {
      ...userData,
      timestamp: new Date().toISOString(),
    });
  },

  async logEngagement(
    action: 'favorites_added' | 'favorites_removed' | 'share_listing' | 'review_submitted',
    data: Record<string, any>
  ) {
    return this.logEvent(action, {
      ...data,
      timestamp: new Date().toISOString(),
    });
  },

  async logFilterUsage(
    filterType: 'hub' | 'category' | 'price' | 'location' | 'sort',
    value: string
  ) {
    return this.logEvent(`${filterType}_filter_applied` as AnalyticsEvent, {
      value,
      timestamp: new Date().toISOString(),
    });
  },

  async logError(error: Error, context: Record<string, any>) {
    return this.logEvent('error_occurred', {
      errorMessage: error.message,
      errorStack: error.stack,
      context,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
    });
  },

  async logPerformance(metric: string, value: number, context?: Record<string, any>) {
    return this.logEvent('performance_metric', {
      metric,
      value,
      context,
      timestamp: new Date().toISOString(),
    });
  },

  async getSellerAnalytics(sellerId: string) {
    const q = query(collection(db, 'system_analytics'), where('sellerId', '==', sellerId));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  }
};
